/**
 * useMCPDebug Hook
 *
 * Provides debugging capabilities for MCP bridge.
 * Debug mode is OFF by default for performance.
 * When enabled, records action logs and state history.
 *
 * SECURITY: All logs sanitize PII (emails become [EMAIL])
 */

import { useState, useRef, useCallback } from 'react';

const MAX_ACTION_LOG = 100;
const MAX_STATE_HISTORY = 50;

/**
 * Sanitize arguments to remove PII
 */
function sanitizeForLog(value) {
  if (typeof value === 'string') {
    // Email pattern
    if (value.includes('@') && value.includes('.')) {
      return '[EMAIL]';
    }
    // Phone pattern (very basic)
    if (/^\+?[\d\s-]{10,}$/.test(value)) {
      return '[PHONE]';
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeForLog);
  }
  if (value && typeof value === 'object') {
    const sanitized = {};
    for (const [key, val] of Object.entries(value)) {
      if (key.toLowerCase().includes('email')) {
        sanitized[key] = '[EMAIL]';
      } else if (key.toLowerCase().includes('password') || key.toLowerCase().includes('token') || key.toLowerCase().includes('secret')) {
        sanitized[key] = '[REDACTED]';
      } else if (key.toLowerCase().includes('phone') || key.toLowerCase().includes('ssn') || key.toLowerCase().includes('dob')) {
        sanitized[key] = '[PII]';
      } else {
        sanitized[key] = sanitizeForLog(val);
      }
    }
    return sanitized;
  }
  return value;
}

export function useMCPDebug() {
  const [enabled, setEnabled] = useState(false);
  const actionLogRef = useRef([]);
  const stateHistoryRef = useRef([]);
  const actionIdRef = useRef(0);

  /**
   * Enable debug mode
   */
  const enable = useCallback(() => {
    setEnabled(true);
    console.log('[MCP Debug] Debug mode enabled');
  }, []);

  /**
   * Disable debug mode
   */
  const disable = useCallback(() => {
    setEnabled(false);
    console.log('[MCP Debug] Debug mode disabled');
  }, []);

  /**
   * Log an action
   */
  const logAction = useCallback((action, args, result, error, duration) => {
    if (!enabled) return;

    const entry = {
      id: actionIdRef.current++,
      timestamp: Date.now(),
      action,
      args: sanitizeForLog(args),
      duration,
      success: !error,
      error: error?.message || null,
      // Don't log full result - could contain PII
      resultType: result ? (Array.isArray(result) ? 'array' : typeof result) : null,
    };

    actionLogRef.current = [entry, ...actionLogRef.current.slice(0, MAX_ACTION_LOG - 1)];

    console.log(`[MCP Debug] Action: ${action}`, entry);
  }, [enabled]);

  /**
   * Take a state snapshot
   */
  const snapshotState = useCallback(() => {
    if (!enabled) return;

    const gc = window.__GETCLEARANCE__;
    if (!gc) return;

    const snapshot = {
      timestamp: Date.now(),
      route: gc.route,
      ready: gc.ready,
      queryCount: Object.keys(gc.queries || {}).length,
      formCount: Object.keys(gc.forms || {}).length,
      lastAction: gc.notifications?.lastAction?.action || null,
      lastError: gc.notifications?.lastError?.error || null,
      ui: gc.ui,
    };

    stateHistoryRef.current = [snapshot, ...stateHistoryRef.current.slice(0, MAX_STATE_HISTORY - 1)];
  }, [enabled]);

  /**
   * Get action log
   */
  const getActionLog = useCallback(() => {
    return [...actionLogRef.current];
  }, []);

  /**
   * Get state history
   */
  const getStateHistory = useCallback(() => {
    return [...stateHistoryRef.current];
  }, []);

  /**
   * Clear all logs
   */
  const clear = useCallback(() => {
    actionLogRef.current = [];
    stateHistoryRef.current = [];
    actionIdRef.current = 0;
    console.log('[MCP Debug] Logs cleared');
  }, []);

  /**
   * Get summary of recent activity
   */
  const getSummary = useCallback(() => {
    const actions = actionLogRef.current;
    const states = stateHistoryRef.current;

    return {
      enabled,
      actionCount: actions.length,
      stateSnapshotCount: states.length,
      successRate: actions.length > 0
        ? (actions.filter(a => a.success).length / actions.length * 100).toFixed(1) + '%'
        : 'N/A',
      avgDuration: actions.length > 0
        ? Math.round(actions.reduce((sum, a) => sum + (a.duration || 0), 0) / actions.length) + 'ms'
        : 'N/A',
      recentActions: actions.slice(0, 5).map(a => a.action),
      recentErrors: actions.filter(a => !a.success).slice(0, 3).map(a => ({ action: a.action, error: a.error })),
    };
  }, [enabled]);

  /**
   * Export logs as JSON (for saving/sharing)
   */
  const exportLogs = useCallback(() => {
    return {
      exportedAt: new Date().toISOString(),
      actions: actionLogRef.current,
      stateHistory: stateHistoryRef.current,
      summary: getSummary(),
    };
  }, [getSummary]);

  return {
    enabled,
    enable,
    disable,
    logAction,
    snapshotState,
    getActionLog,
    getStateHistory,
    clear,
    getSummary,
    exportLogs,
  };
}
